import { useCallback, useMemo } from 'react';
import { getAuthUserMail } from 'utils/helperMethods';
import {
    normalizePpaTableRowAction,
    PpaTableRowAction,
    PpaTableRowType,
} from 'types/ppa';
import type { InternalTableRecord } from '../types';

type ProjectPermissionRecord = {
    project_id: number;
    created_by?: string;
};

type UseScenarioPermissionsParams = {
    projects: ProjectPermissionRecord[];
};

export const useScenarioPermissions = ({ projects }: UseScenarioPermissionsParams) => {
    const currentUserMail = useMemo(
        () =>
            String(getAuthUserMail() ?? '')
                .trim()
                .toLowerCase(),
        [],
    );

    const projectOwnerAccessById = useMemo(
        () =>
            projects.reduce<Record<number, boolean>>((accumulator, project) => {
                const projectCreatorMail = String(project.created_by ?? '')
                    .trim()
                    .toLowerCase();
                accumulator[project.project_id] = Boolean(
                    projectCreatorMail && projectCreatorMail === currentUserMail,
                );
                return accumulator;
            }, {}),
        [currentUserMail, projects],
    );

    const canAccessAction = useCallback(
        (record: InternalTableRecord, actionLabel: string) => {
            const normalizedAction = normalizePpaTableRowAction(actionLabel);
            if (!normalizedAction) {
                return true;
            }
            const isProjectOwner = Boolean(projectOwnerAccessById[record.project_id]);

            if (record.row_type === PpaTableRowType.Project && normalizedAction === PpaTableRowAction.Delete) {
                return false;
            }

            if (record.row_type === PpaTableRowType.Project && normalizedAction === PpaTableRowAction.Edit) {
                return isProjectOwner;
            }

            if (record.row_type === PpaTableRowType.Scenario) {
                if (
                    normalizedAction === PpaTableRowAction.Edit ||
                    normalizedAction === PpaTableRowAction.Delete
                ) {
                    const scenarioCreatorMail = String(record.created_by ?? '')
                        .trim()
                        .toLowerCase();
                    return Boolean(
                        scenarioCreatorMail && scenarioCreatorMail === currentUserMail,
                    );
                }
            }

            return true;
        },
        [projectOwnerAccessById, currentUserMail],
    );

    return { canAccessAction, projectOwnerAccessById, currentUserMail };
};
